/**
 * The comment-chat popup's decision layer (SPEC `Comment Generation` §8).
 *
 * Everything the dialog could get wrong lives here as one pure function, for the same reason the
 * notification card has a plan module: the web suites run in `node` with no DOM, so what can be
 * tested is the plan, not the markup. `CommentChatDialog` renders this — it decides nothing.
 *
 * The states it has to keep straight, and why each is not obvious:
 *
 *  - **Setup wins over everything.** #77 says the slot is not ready ⇒ the popup shows the server's
 *    `readyReason` instead of a chat, even when the post already has one. A `409` from a start/send
 *    (a stale readiness cache) arrives as `conflictReason` and forces the same view.
 *  - **The empty chat is a chat, not a fourth view.** A popup opens on an empty box: the first
 *    preset click or typed line is what creates the conversation (#83). It is the `chat` view with
 *    no bubbles, so the setup/loading/error precedence above stays in one place.
 *  - **"New chat" is lazy and client-side.** Clicking it hides the live conversation by id; the
 *    server archive happens inside #83's transaction when the first message of the new chat is
 *    sent. Closing the popup without sending leaves the old chat active — nothing was destroyed.
 *    Hiding is by **id**, not a flag: if a start fails after its transaction (a 502 on the
 *    generation), the refetched chat carries a *new* conversation id with an unanswered user tail,
 *    and an id comparison shows it automatically where a flag would keep hiding it.
 *  - **An unanswered user tail is an error state with no error.** The generation is a separate
 *    transaction from the user-message insert (§7), so a crash — or an f5 after a 502 — leaves a
 *    chat whose last message is the user's. That is derivable, and it must offer Retry exactly like
 *    a live failure does.
 *  - **Retry means two different endpoints.** With a visible conversation it is #85 (regenerate off
 *    the remaining history); with none — the very first generation failed before anything was
 *    created, or the user is in a fresh empty chat — it is #83 again.
 *  - **Regenerate rides the newest assistant only**, and only while idle: it is destructive (the
 *    bubble it sits on is superseded), so it may never appear on a message the user has already
 *    replied to.
 */

import type { AiChatRole, AiPreset, AiTabDTO, CommentChatDTO } from '@lw/shared';

/** Which mutation is in flight, if any — the dialog's three `useMutation`s, flattened. */
export type ChatPendingKind = 'none' | 'start' | 'send' | 'regenerate';

/** Which endpoint the error row's Retry button calls. */
export type ChatRetryKind = 'start' | 'regenerate';

/** Which endpoint the composer (and a preset click) posts to — #83 opens a chat, #84 continues one. */
export type ChatComposerTarget = 'start' | 'send';

export type ChatViewKind = 'loading' | 'setup' | 'chat';

/**
 * Fallback for a `readyReason`/`details.reason` the server left empty. Client copy only ever fills
 * a hole the server left — it never rephrases a sentence the server did send (§12.5).
 */
export const CHAT_SETUP_FALLBACK_REASON = 'Finish the comment-generation setup first.';

/** The one message with no server original: a reply that never landed leaves nothing to quote. */
export const CHAT_UNANSWERED_MESSAGE = 'The reply never arrived.';

/** The empty chat's only copy — a state the server has no sentence for, because it has no chat yet. */
export const CHAT_EMPTY_HINT = 'Pick a preset below or describe the comment you want.';

export type ChatBubblePlan = {
  /** `ai_messages.id` — stable across a refetch, so it is the React key. */
  id: number;
  role: AiChatRole;
  /** Assistant content is Markdown, user content is plain text; both render verbatim. */
  content: string;
  /** Exactly one bubble per plan can carry it: the newest assistant, while idle. */
  canRegenerate: boolean;
};

export type ChatErrorPlan = {
  /** The server's message verbatim, or `CHAT_UNANSWERED_MESSAGE` for a derived one. */
  message: string;
  retry: ChatRetryKind;
};

export type ChatPlan = {
  view: ChatViewKind;
  /** Non-null exactly when `view === 'setup'`. */
  setupReason: string | null;
  /** Author + one-line preview, whenever #82 has answered — the header line survives every view. */
  context: { authorName: string; preview: string } | null;
  /** The target of #84/#85; `null` in an empty chat, and while one is hidden by "New chat". */
  conversationId: number | null;
  bubbles: ChatBubblePlan[];
  /** The chat view with nothing in it: no conversation to show, so the next message starts one. */
  emptyChat: boolean;
  /** The typing-indicator row: a generation is running. */
  typing: boolean;
  error: ChatErrorPlan | null;
  /** The derived half of `error` — exposed so the dialog never re-derives it. */
  tailIsUnansweredUser: boolean;
  showComposer: boolean;
  /** Where the composer and every preset button send. */
  composerTarget: ChatComposerTarget;
  inputDisabled: boolean;
  /** The stored presets, in order — rendered as the composer's buttons, names verbatim. */
  presets: AiPreset[];
  showPresets: boolean;
  presetsDisabled: boolean;
  showNewChat: boolean;
  canStartNewChat: boolean;
};

export type ChatStateInput = {
  /** #82's payload; `undefined` while the query is still in flight. */
  chat: CommentChatDTO | undefined;
  /** #77's payload; `undefined` while readiness is still unknown. */
  readiness: AiTabDTO | undefined;
  pending: ChatPendingKind;
  /** The last generation failure, server message verbatim; `null` when the last call was clean. */
  errorMessage: string | null;
  /** `details.reason` of a not-ready `409` — the backstop for a stale readiness cache. */
  conflictReason?: string | null;
  /** The conversation "New chat" is hiding, by id; `null` when nothing is hidden. */
  hiddenConversationId?: number | null;
};

export function commentChatPlan(input: ChatStateInput): ChatPlan {
  const { chat, readiness, pending, errorMessage } = input;
  const conflictReason = input.conflictReason ?? null;
  const hiddenConversationId = input.hiddenConversationId ?? null;

  const context =
    chat === undefined ? null : { authorName: chat.post.authorName, preview: chat.post.preview };
  // What the popup is actually showing. A hidden conversation is still in the cache — it just is
  // not this chat any more, so neither its id nor its messages may leak into the plan.
  const cached = chat?.conversation ?? null;
  const visibleConversation = cached !== null && cached.id !== hiddenConversationId ? cached : null;
  const conversationId = visibleConversation?.id ?? null;
  const presets = readiness?.prompts.presets ?? [];
  const idle = pending === 'none';

  // Setup first: a not-ready slot has no chat to show, whichever way we learned about it.
  if (conflictReason !== null) {
    return setupPlan(context, conversationId, presets, conflictReason);
  }
  if (readiness !== undefined && !readiness.ready) {
    return setupPlan(context, conversationId, presets, readiness.readyReason);
  }
  if (readiness === undefined || chat === undefined) {
    return {
      view: 'loading',
      setupReason: null,
      context,
      conversationId,
      bubbles: [],
      emptyChat: false,
      typing: false,
      error: null,
      tailIsUnansweredUser: false,
      showComposer: false,
      composerTarget: 'start',
      inputDisabled: true,
      presets,
      showPresets: false,
      presetsDisabled: true,
      showNewChat: false,
      canStartNewChat: false,
    };
  }

  const emptyChat = visibleConversation === null;
  // The messages belong to the visible conversation or to nothing: a hidden one's turns are the old
  // chat's, and the empty state has none of its own.
  const messages = emptyChat ? [] : chat.messages;
  const tail = messages.length === 0 ? null : messages[messages.length - 1];
  // Only the tail can be regenerated: an older assistant already has a reply under it, and
  // superseding it would rewrite history the user has answered.
  const regenerateId = idle && tail !== null && tail.role === 'assistant' ? tail.id : null;
  const tailIsUnansweredUser = idle && tail !== null && tail.role === 'user';

  // With no visible conversation the only thing to retry is the start itself — there is nothing to
  // regenerate off, because nothing (visible) was created.
  const retry: ChatRetryKind = conversationId === null ? 'start' : 'regenerate';
  const error: ChatErrorPlan | null =
    errorMessage !== null
      ? { message: errorMessage, retry }
      : tailIsUnansweredUser
        ? { message: CHAT_UNANSWERED_MESSAGE, retry: 'regenerate' }
        : null;

  return {
    view: 'chat',
    setupReason: null,
    context,
    conversationId,
    bubbles: messages.map((message) => ({
      id: message.id,
      role: message.role,
      content: message.content,
      canRegenerate: message.id === regenerateId,
    })),
    emptyChat,
    typing: !idle,
    error,
    tailIsUnansweredUser,
    showComposer: true,
    composerTarget: emptyChat ? 'start' : 'send',
    // Only a live generation locks the field. A failed one does not: typing a different follow-up
    // is a legitimate answer to "that didn't work" — and in an empty chat, submitting is what
    // creates the conversation, so there is nothing else to wait for.
    inputDisabled: !idle,
    presets,
    showPresets: presets.length > 0,
    presetsDisabled: !idle,
    // Nothing to start over from: the empty chat *is* the new chat.
    showNewChat: !emptyChat,
    canStartNewChat: idle,
  };
}

function setupPlan(
  context: ChatPlan['context'],
  conversationId: number | null,
  presets: AiPreset[],
  reason: string | null,
): ChatPlan {
  return {
    view: 'setup',
    setupReason: reason !== null && reason.length > 0 ? reason : CHAT_SETUP_FALLBACK_REASON,
    context,
    conversationId,
    bubbles: [],
    emptyChat: false,
    typing: false,
    error: null,
    tailIsUnansweredUser: false,
    showComposer: false,
    composerTarget: 'start',
    inputDisabled: true,
    presets,
    showPresets: false,
    presetsDisabled: true,
    showNewChat: false,
    canStartNewChat: false,
  };
}
